import type { TourPage, TourSection } from "@/components/demo-tour/sections"

const inbox: TourPage = { kind: "business_inbox" }
const conversation: TourPage = { kind: "business_inbox_conversation" }
const leads: TourPage = { kind: "business_leads" }
const leadDetail: TourPage = { kind: "business_lead_detail" }
const reporting: TourPage = { kind: "business_reporting" }

/** Customer Hub walkthrough. The Leads and Reporting copy is ported from the
 *  production Product Fruits "Growbotik Customer Hub" flows; the first two
 *  sections walk the inbox that replaced To-Dos (ClickUp 14zbjdpmbmq). The
 *  brief steps are optional: they show when the top conversation's first
 *  open to-do is the reviews setup, and are skipped otherwise. */
export const HUB_TOUR_SECTIONS: TourSection[] = [
  {
    id: "hub_intro",
    label: "Customer Hub",
    steps: [
      {
        page: inbox,
        target: "hub-sidebar",
        title: "🏠 Where the plan goes live",
        body: (
          <>
            Once the plan is approved, this is the client&rsquo;s home base. The
            left menu holds it all: the <b>Growth Plan</b>, their{" "}
            <b>Business Profile</b>, the <b>Inbox</b> with everything to launch,
            incoming <b>Leads</b>, and <b>Reporting</b>.
          </>
        ),
        btn: "Show me the inbox",
      },
      {
        page: inbox,
        target: "hub-inbox-main",
        title: "📥 One inbox for everything",
        body: "Each service is one conversation: the team's messages and the client's to-dos side by side. The bar tracks progress to going live, and anything waiting on the client sits at the top.",
        btn: "Open the first one",
      },
      {
        page: inbox,
        target: "inbox-first-conversation",
        action: "click",
        title: "▶️ Open a conversation",
        body: (
          <>
            The top conversation is the one waiting on the client.{" "}
            <b>Click it</b> to see what is inside.
          </>
        ),
      },
    ],
  },
  {
    id: "hub_review",
    label: "Inbox: a To-Do Example",
    steps: [
      {
        page: conversation,
        target: "inbox-open-card",
        // The copy points at the team's message under the card; keep it visible.
        placement: "above",
        title: "✅ To-dos live in the conversation",
        body: "Each task is a card in the stream, next to the messages about it. Quick ones are filled in right here, and a question about one goes to the team in the same conversation.",
        btn: "Show me more",
      },
      {
        page: conversation,
        target: "inbox-open-to-fill",
        action: "click",
        optional: true,
        title: "📝 Longer forms get their own page",
        body: (
          <>
            This one takes a few minutes, so it opens full page, one back arrow
            from the conversation. <b>Click Open to fill in</b>.
          </>
        ),
      },
      {
        page: conversation,
        target: "todo-plan-why",
        optional: true,
        title: "📋 Every task explains itself",
        body: (
          <>
            <b>The Plan</b> says what is being set up. The <b>Why</b> ties it
            back to what the Gap Analysis found, so nothing feels like busywork.
          </>
        ),
        btn: "Show me more",
      },
      {
        page: conversation,
        optional: true,
        target: "todo-review-sites",
        title: "⭐ Pick the review sites",
        body: (
          <>
            The business adds the sites it wants reviews on (Google, Yelp,
            Facebook) and pastes each review link.{" "}
            <b>&ldquo;How to get review links&rdquo;</b> walks them through
            finding those.
          </>
        ),
        btn: "Got it",
      },
      {
        page: conversation,
        optional: true,
        target: "todo-logo-upload",
        title: "🎨 Make it theirs",
        body: "The logo goes on the review page customers see, so the request looks like the business and not a generic form.",
        btn: "Next",
      },
      {
        page: conversation,
        optional: true,
        target: "todo-feedback-email",
        title: "🛡️ Catch complaints privately",
        body: "Unhappy customers get routed straight to the business by email instead of posting publicly. Add the address that should receive them, and the rating stays protected.",
        btn: "Nice touch",
      },
      {
        page: conversation,
        optional: true,
        target: "todo-request-timing",
        title: "⏰ Time the ask right",
        body: (
          <>
            <b>On payment</b> suits jobs that finish at checkout.{" "}
            <b>Days after payment</b> fits trades like roofing, where the ask
            lands once the work is done and the customer is happy.
          </>
        ),
        btn: "Makes sense",
      },
      {
        page: conversation,
        optional: true,
        target: "todo-submit",
        title: "🚀 What happens after submit",
        body: "The team takes it from here. Anything to approve comes back as a new card, progress shows up as messages in this same conversation, and the client is notified when it is live.",
        btn: "On to Leads",
      },
    ],
  },
  {
    id: "hub_leads",
    label: "Leads Overview",
    steps: [
      {
        page: inbox,
        target: "hub-nav-leads",
        action: "click",
        title: "📇 Where the leads land",
        body: (
          <>
            Every call and form the plan brings in shows up here automatically,
            with name, phone and email ready to go. <b>Click Leads</b> to open
            it.
          </>
        ),
      },
      {
        page: leads,
        target: "leads-filters",
        title: "🔎 Find anyone fast",
        body: "Search by name, email or phone, narrow by date, or filter by Lead Type and Channel as the list grows.",
        btn: "Next",
      },
      {
        page: leads,
        target: "leads-row",
        action: "click",
        title: "👤 One row, one lead",
        body: (
          <>
            Each row opens the full story: where the lead came from and every
            conversation they have had. <b>Click one</b> to look inside.
          </>
        ),
      },
      {
        page: leadDetail,
        target: "lead-interactions",
        title: "🧾 The whole story",
        body: "Calls come with a summary, recording and transcript. Forms show exactly what was submitted. Whoever follows up already knows the full picture.",
        btn: "See the results",
      },
    ],
  },
  {
    id: "hub_reporting",
    label: "Reporting Overview",
    steps: [
      {
        page: leads,
        target: "hub-nav-reporting",
        action: "click",
        title: "📈 Proof it is working",
        body: (
          <>
            Leads in, jobs booked, and the channels driving both. This is where
            the plan proves itself. <b>Click Reporting</b> to open it.
          </>
        ),
      },
      {
        page: reporting,
        target: "report-kpis",
        title: "🔢 The three headline numbers",
        body: (
          <>
            <b>Total Leads</b>, <b>Booked Meetings</b> and the{" "}
            <b>Conversion Rate</b> between them. Green shows the trend against
            the previous period, and the selector up top spans 24 hours to 12
            months.
          </>
        ),
        btn: "Show me more",
      },
      {
        page: reporting,
        target: "report-form-channels",
        title: "📝 Where the form leads come from",
        body: "Google Business Profile, organic search, Google Ads, Facebook and the rest. The longer the bar, the more that channel is producing.",
        btn: "And the calls?",
      },
      {
        page: reporting,
        target: "report-call-channels",
        title: "📞 And where the calls come from",
        body: "The same read for phone calls, since plenty of customers would rather dial than type. Together the two charts show which channels carry their weight.",
        btn: "One more",
      },
      {
        page: reporting,
        target: "report-booked-channels",
        title: "📅 Which channels book jobs",
        body: "The quality read: not just who sends leads, but whose leads reach the calendar. That is where the budget should go.",
        btn: "Finish tour ✓",
      },
    ],
  },
]
